/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppBrand } from '@vaas/react-ui-kit';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon } from '@/components/Icon';
import { tourForPath } from '@/features/tour/tourSteps';
import { useUiStore } from '@/stores/uiStore';
import { useCanSwitchRoles, useIsInternalUser } from '@/lib/userRole';
import { presetFor } from '@/lib/roles';
import { useRoleView } from '@/stores/roleView';
import { useThemeToggle } from './useThemeToggle';
import { loginRequest } from '@/auth/msalConfig';
import { Menu } from './Menu';
import { LanguageMenu } from './LanguageMenu';
import { RoleViewMenu } from './RoleViewMenu';
import { SettingsModal } from './SettingsModal';
import { ReleaseNotesModal } from './ReleaseNotesModal';
import { AboutModal } from './AboutModal';
import { ProfileModal } from './ProfileModal';
import { VehiclePicker } from './VehiclePicker';

/** Help links. */
const USER_GUIDE_URL = '/user-guide/ewd-user-guide-v03.pdf';
const USER_GUIDE_VERSION = 'v03';
/** Internal GM Slack workspace; dealer accounts cannot join it, so the link is internal-only. */
const SLACK_CHANNEL_URL = 'https://gm.enterprise.slack.com/archives/C093EFDL2LW';

type OpenMenu = 'help' | 'account' | 'language' | 'roleView' | 'more' | null;

/** Top application bar: brand, theme toggle, settings, help, account. */
export function Topbar() {
  const { t, i18n } = useTranslation();
  const { resolved, toggle: toggleTheme } = useThemeToggle();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mode, isAuthenticated, name, initials, account, signIn, signOut } = useAuth();
  const showPicker = pathname.startsWith('/schematic-explorer') || pathname.startsWith('/mecl');
  // The standalone 3D viewer (launched from MECL) shows the fixed vehicle context, read-only.
  const showVehicleInfo = pathname.startsWith('/viewer-3d');
  // The discussion channel lives in the GM Slack workspace, which dealers have no access to.
  // The hook reads as external until the account resolves, so the link never flashes for them.
  const showDiscussion = useIsInternalUser();
  const toggleNav = useUiStore((s) => s.toggleNav);
  const navOpen = useUiStore((s) => s.navOpen);
  const openFeedback = useUiStore((s) => s.openFeedback);
  const openResources = useUiStore((s) => s.openResources);
  const startTour = useUiStore((s) => s.startTour);
  const releaseNotesOpen = useUiStore((s) => s.releaseNotesOpen);
  const openReleaseNotes = useUiStore((s) => s.openReleaseNotes);
  const closeReleaseNotes = useUiStore((s) => s.closeReleaseNotes);
  const aboutOpen = useUiStore((s) => s.aboutOpen);
  const openAbout = useUiStore((s) => s.openAbout);
  const closeAbout = useUiStore((s) => s.closeAbout);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Single open-menu value: only one Topbar dropdown at a time.
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  // A folded menu opened from "More", whose own trigger is hidden: it takes focus, and gives it
  // back to "More" when it closes.
  const moreRef = useRef<HTMLButtonElement>(null);
  const [handoff, setHandoff] = useState<OpenMenu>(null);
  const setMenu = (menu: OpenMenu) => (open: boolean) => {
    setHandoff(null);
    setOpenMenu(open ? menu : null);
  };
  const openFromMore = (menu: 'language' | 'roleView') => {
    setHandoff(menu);
    setOpenMenu(menu);
  };
  const close = () => setOpenMenu(null);
  // "More" repeats the secondary actions for when they have folded out of the bar (narrow
  // screens, or a vehicle in the header). It names each one and its current value in words.
  const canSwitchRoles = useCanSwitchRoles();
  const rolePreset = presetFor(useRoleView((s) => s.presetId));
  const themeLabel = t(`settings.theme${resolved === 'dark' ? 'Dark' : 'Light'}`);

  return (
    <header className="topbar" id="app-header">
      <button
        type="button"
        className="tb-burger"
        title={t('topbar.menu', 'Menu')}
        aria-label={t('topbar.menu', 'Menu')}
        aria-expanded={navOpen}
        aria-controls="app-rail"
        onClick={toggleNav}
      >
        <Icon name="menu" />
      </button>

      <AppBrand
        className="tb-brand"
        title={t('topbar.brandTitle')}
        subtitle={t('topbar.brandSub')}
        homeLabel={t('nav.home')}
        onNavigate={(href) => navigate(href)}
      />

      <div className="tb-sp" />
      {showPicker && <VehiclePicker />}
      {showVehicleInfo && <VehiclePicker readOnly />}
      <div className="tb-sp" />

      {/*
        Shows the theme that is on, not the one a click would bring - matching the kit's
        `ThemeToggle`, which VEIS uses. The two apps used to disagree, so the same moon meant
        "you are in dark" in one and "click for dark" in the other.

        The current theme is named in `aria-label` and deliberately not in `title`: the tooltip layer
        reads `title` once, when the pointer arrives, so text that changes on click would still read
        "(Dark)" over a sun after the user had already flipped back to light. The icon says which
        theme is on for anyone who can see it; the label says it for anyone who cannot.

        Not the kit component itself: its 40px pill would sit oddly among the four `tb-ic` buttons
        beside it. The behaviour is shared; the chrome is this topbar's.
      */}
      <div className="tb-secondary">
        <button
          className="tb-ic"
          title={t('topbar.toggleTheme')}
          aria-label={`${t('topbar.toggleTheme')} (${themeLabel})`}
          data-tour="theme"
          onClick={toggleTheme}
        >
          <Icon name={resolved === 'dark' ? 'moon' : 'sun'} />
        </button>

        <RoleViewMenu
          open={openMenu === 'roleView'}
          onOpenChange={setMenu('roleView')}
          handoffFrom={handoff === 'roleView' ? moreRef : undefined}
        />

        <LanguageMenu
          open={openMenu === 'language'}
          onOpenChange={setMenu('language')}
          handoffFrom={handoff === 'language' ? moreRef : undefined}
        />

        <button
          className="tb-ic"
          title={t('topbar.settings')}
          data-tour="settings"
          onClick={() => setSettingsOpen(true)}
        >
          <Icon name="settings" />
        </button>
      </div>

      <Menu
        className="tb-more-menu"
        open={openMenu === 'more'}
        onOpenChange={setMenu('more')}
        trigger={({ open, toggle }) => (
          <button
            ref={moreRef}
            type="button"
            className="tb-ic tb-more"
            title={t('topbar.more', 'More')}
            aria-label={t('topbar.more', 'More')}
            data-tour="more"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            <Icon name="more_vert" />
          </button>
        )}
      >
        <div className="menu-items">
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              toggleTheme();
            }}
          >
            <Icon name={resolved === 'dark' ? 'moon' : 'sun'} size={18} /> {t('topbar.toggleTheme')}
            <span className="menu-item-meta">{themeLabel}</span>
          </button>
          {canSwitchRoles && (
            <button type="button" className="menu-item" onClick={() => openFromMore('roleView')}>
              <Icon name="eye" size={18} /> {t('roles.switcher.title')}
              <span className="menu-item-meta">
                {rolePreset ? t(rolePreset.labelKey) : t('roles.switcher.allRoles.label')}
              </span>
            </button>
          )}
          <button type="button" className="menu-item" onClick={() => openFromMore('language')}>
            <Icon name="globe" size={18} /> {t('language.title')}
            <span className="menu-item-meta">{i18n.language.toUpperCase()}</span>
          </button>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              close();
              setSettingsOpen(true);
            }}
          >
            <Icon name="settings" size={18} /> {t('topbar.settings')}
          </button>
        </div>
      </Menu>

      <Menu
        open={openMenu === 'help'}
        onOpenChange={setMenu('help')}
        trigger={({ open, toggle }) => (
          <button
            className="tb-ic"
            title={t('topbar.help')}
            data-tour="help"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            <Icon name="help" />
          </button>
        )}
      >
        <div className="menu-head">
          <div>
            <div className="menu-name">{t('topbar.help')}</div>
            <div className="menu-sub">{t('topbar.helpSub')}</div>
          </div>
        </div>
        <div className="menu-items">
          <button
            className="menu-item"
            onClick={() => {
              close();
              startTour(tourForPath(pathname));
            }}
          >
            <Icon name="compass" size={18} /> {t('topbar.quickTour')}
          </button>
          <a
            className="menu-item"
            href={USER_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            download={`ewd-user-guide-${USER_GUIDE_VERSION}.pdf`}
            onClick={close}
          >
            <Icon name="book" size={18} /> {t('topbar.userGuide', { version: USER_GUIDE_VERSION })}
          </a>
          <button
            className="menu-item"
            onClick={() => {
              close();
              openResources();
            }}
          >
            <Icon name="external" size={18} /> {t('topbar.resourceLinks')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              openReleaseNotes();
            }}
          >
            <Icon name="spark" size={18} /> {t('topbar.whatsNew')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              openFeedback();
            }}
          >
            <Icon name="chat" size={18} /> {t('topbar.sendFeedback')}
          </button>
          {showDiscussion && (
            <a
              className="menu-item"
              href={SLACK_CHANNEL_URL}
              target="_blank"
              rel="noreferrer"
              onClick={close}
            >
              <Icon name="external" size={18} /> {t('topbar.discussion')}
            </a>
          )}
        </div>
      </Menu>

      <Menu
        open={openMenu === 'account'}
        onOpenChange={setMenu('account')}
        trigger={({ open, toggle }) => (
          <button
            className="av"
            title={t('topbar.account')}
            data-tour="account"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
          >
            {initials || 'JT'}
          </button>
        )}
      >
        <div className="menu-head">
          <span className="menu-av">{initials || 'JT'}</span>
          <div>
            <div className="menu-name">{name ?? t('topbar.developer')}</div>
            {account?.username && <div className="menu-sub">{account.username}</div>}
            <div className="menu-role">
              {account?.gmin
                ? t('topbar.gmin', { gmin: account.gmin })
                : mode === 'mock'
                  ? t('topbar.devAuth')
                  : t('topbar.entra')}
            </div>
          </div>
        </div>
        <div className="menu-items">
          <button
            className="menu-item"
            onClick={() => {
              close();
              setProfileOpen(true);
            }}
          >
            <Icon name="user" size={18} /> {t('topbar.viewProfile')}
          </button>
          <button
            className="menu-item"
            onClick={() => {
              close();
              openAbout();
            }}
          >
            <Icon name="info" size={18} /> {t('topbar.about')}
          </button>
          {isAuthenticated ? (
            <button
              className="menu-item neg"
              onClick={() => {
                close();
                signOut();
              }}
            >
              <Icon name="close" size={18} /> {t('topbar.signOut')}
            </button>
          ) : (
            <button
              className="menu-item"
              onClick={() => {
                close();
                signIn(loginRequest);
              }}
            >
              <Icon name="user" size={18} /> {t('topbar.signIn')}
            </button>
          )}
        </div>
      </Menu>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
      {releaseNotesOpen && <ReleaseNotesModal onClose={closeReleaseNotes} />}
      {aboutOpen && <AboutModal onClose={closeAbout} />}
    </header>
  );
}
